import {
  CSSProperties,
  RefObject,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";

const DROPDOWN_GAP = 4;

const useFixedDropdownPosition = (
  containerRef: RefObject<HTMLElement>,
  isOpen: boolean,
  enabled: boolean,
  menuHeight: number,
  forceOpenUp = false,
  forceOpenDown = false,
) => {
  const [style, setStyle] = useState<CSSProperties>();

  const updatePosition = useCallback(() => {
    const container = containerRef.current;

    if (!container || !enabled) {
      setStyle(undefined);
      return;
    }

    const rect = container.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
    const openUp =
      !forceOpenDown &&
      (forceOpenUp || (spaceBelow < 120 && rect.top > spaceBelow));
    const availableHeight = openUp
      ? rect.top - DROPDOWN_GAP * 2
      : spaceBelow - DROPDOWN_GAP;
    const maxHeight = Math.min(
      menuHeight,
      Math.max(forceOpenDown ? 0 : 80, availableHeight),
    );
    const top = openUp
      ? Math.max(DROPDOWN_GAP, rect.top - maxHeight - DROPDOWN_GAP)
      : rect.bottom + DROPDOWN_GAP;

    let triggerClipped = false;
    if (forceOpenDown) {
      for (
        let parent = container.parentElement;
        parent;
        parent = parent.parentElement
      ) {
        if (
          /auto|scroll|hidden|clip/.test(
            window.getComputedStyle(parent).overflowY,
          )
        ) {
          const bounds = parent.getBoundingClientRect();
          if (rect.bottom <= bounds.top || rect.top >= bounds.bottom) {
            triggerClipped = true;
            break;
          }
        }
      }
    }

    const nextStyle: CSSProperties = {
      position: "fixed",
      top,
      bottom: "auto",
      left: rect.left,
      width: rect.width,
      maxHeight,
      visibility:
        forceOpenDown && (maxHeight === 0 || rect.bottom <= 0 || triggerClipped)
          ? "hidden"
          : undefined,
    };
    setStyle((previous) =>
      previous &&
      Object.keys(nextStyle).every(
        (key) =>
          previous[key as keyof CSSProperties] ===
          nextStyle[key as keyof CSSProperties],
      )
        ? previous
        : nextStyle,
    );
  }, [containerRef, enabled, forceOpenUp, forceOpenDown, menuHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !enabled) {
      return;
    }

    updatePosition();
    // Layout moves (cloning, reordering, drag transforms) do not resize the trigger.
    let animationFrame: number;
    const followTrigger = () => {
      updatePosition();
      animationFrame = window.requestAnimationFrame(followTrigger);
    };
    animationFrame = window.requestAnimationFrame(followTrigger);
    const resizeObserver = new ResizeObserver(updatePosition);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [containerRef, enabled, isOpen, updatePosition]);

  return style;
};

export default useFixedDropdownPosition;
